import { HasIdAndName, NestedOption, TreeSelectField, useComputed } from "@homebound/beam";
import { ListFieldState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useMemo } from "react";
import { NamedDevelopmentCohortsProjectsFragment } from "src/fragments/circularFragments.generated";
import { ProjectDocumentAssociationInput, SaveDocumentInput } from "src/generated/graphqlTypes";

export type BoundAssociatedProjectsTreeSelectProps<T> = {
  development: NamedDevelopmentCohortsProjectsFragment;
  field: ListFieldState<ProjectDocumentAssociationInput>;
  readOnly?: boolean;
  expandAll?: boolean;
};

export function BoundAssociatedProjectsTreeSelect<I extends Pick<SaveDocumentInput, "projectDocumentAssociations">>(
  props: BoundAssociatedProjectsTreeSelectProps<I>,
) {
  const { development, field, readOnly = false, expandAll = false } = props;

  // Build a 3-level tree: development -> cohort -> project
  const options: NestedOption<HasIdAndName>[] = useMemo(
    () => [
      {
        id: development.id,
        name: development.name,
        children: (development.cohorts ?? [])
          .filter((c) => !c.projects.isEmpty)
          .map((cohort) => ({
            id: cohort.id,
            name: cohort.name,
            children: cohort.projects.map((p) => ({ id: p.id, name: p.name })),
          })),
      },
    ],
    [development],
  );

  // Currently selected project IDs (entries without delete flag)
  const selectedValues = useComputed(
    () => (field.value ?? []).filter((v) => !v.delete).map((v) => v.projectId),
    [field],
  );

  function onSelect({ leaf }: { leaf: { values: (string | number)[] } }) {
    const allProjectIds = development.cohorts?.flatMap((c) => c.projects.map((p) => p.id)) ?? [];
    const newSelectedIds = new Set(leaf.values as string[]);
    const previouslySelectedIds = new Set((field.value ?? []).filter((v) => !v.delete).map((v) => v.projectId));
    const newValue: ProjectDocumentAssociationInput[] = allProjectIds
      .map((projectId) => {
        if (newSelectedIds.has(projectId)) return { projectId };
        if (previouslySelectedIds.has(projectId)) return { projectId, delete: true };
        return undefined;
      })
      .compact();
    field.set(newValue);
    field.maybeAutoSave();
  }

  return (
    <Observer>
      {() => (
        <TreeSelectField
          label="Projects"
          options={options}
          values={selectedValues}
          onSelect={onSelect}
          readOnly={readOnly}
          placeholder="Select Projects"
          {...(!expandAll ? { defaultCollapsed: true } : {})}
        />
      )}
    </Observer>
  );
}
